import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { IconButton, Menu, MenuItem } from '@material-ui/core'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import Icon from '../../../atoms/Icon'
import Tooltip from '../../../atoms/Tooltip'
import { useAppContext } from '../../../../redux/slices/appContext'
import FlexRow from '../../../molecules/FlexRow'

function DDPEmailWidget ({
  pipeline,
  variables,
  size,
  accessor,
  label,
  tooltip,
  className,
  style
}) {
  const { isSummitUser } = useAppContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)
  const _emailData = useMemo(() => {
    const phoneData = get(data, accessor)
    if (Array.isArray(phoneData)) {
      return phoneData
    } else {
      return phoneData ? [phoneData] : []
    }
  }, [data, accessor])

  const [anchorEl, setAnchorEl] = useState()
  const open = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.target)
  }, [setAnchorEl])
  const close = useCallback(() => setAnchorEl(null), [])

  if (isLoading) {
    return null
  }

  if (!_emailData) {
    return null
  }

  const disabled = !_emailData?.length

  return (
    <div className='flex flex-col justify-center items-center gap-sm' ref={anchorEl}>
      <Tooltip title={tooltip}>
        <IconButton className={className} style={{ ...(style || {}), backgroundColor: disabled ? 'var(--surface-default-disabled-bg)' : 'var(--surface-default-normal-bg)' }} onClick={open} disabled={disabled}>
          <Icon name='mail' customSize={size} color={disabled ? 'var(--text-color-disabled)' : 'var(--text-color-main)'} />
        </IconButton>
      </Tooltip>
      <div className='font-size-xs' style={{ color: disabled ? 'var(--text-color-disabled)' : 'var(--text-color-main)' }}>{label}</div>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
      >
        {(_emailData || []).filter(a => !a.disabled).map((email, index) => (
          <a href={isSummitUser ? 'mailto:' : `mailto:${email.address}`} onClick={close} key={`email_${index}`}>
            <MenuItem>
              <FlexRow>
                <Icon name='mail' />
                <div>{email.custom_type_title || email.address}</div>
                {email.is_preferred || email.is_primary ? (
                  <span style={{ fontStyle: 'italic' }}>
                    {`(${email.is_primary ? 'Primary' : ''}${email.is_primary && email.is_preferred ? ', ' : ''}${email.is_preferred ? 'Preferred' : ''})`}
                  </span>
                ) : null}
              </FlexRow>
            </MenuItem>
          </a>
        ))}
      </Menu>
    </div>
  )
}

DDPEmailWidget.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  variables: PropTypes.object,

  /** The size of the phone icon */
  size: PropTypes.number,

  /** The action label */
  label: PropTypes.string,

  /** The tool tip */
  tooltip: PropTypes.string,

  /** Gets the path to the phone data */
  accessor: PropTypes.string,

  className: PropTypes.string,
  style: PropTypes.object
}

DDPEmailWidget.defaultProps = {
  size: 20,
  label: 'Mail',
  tooltip: 'Send an email',
  className: 'radius-round flex items-center justify-center opal-surface-default hover color-neutral-500 shadow-md',
  style: {
    width: '50px',
    height: '50px'
  }
}

export default DDPEmailWidget
